/* eslint-disable react/display-name */

import axiosInstance, {BASE_URL} from "./service-instance";
import axios from "axios";
import {
    createFormData,
    createUrl,
    setAuthToken,
    checkAuthToken,
    getAuthToken,
    removeAuthToken
} from "utilities";

import {
    CLIENT_ID,
    CLIENT_SECRET,
    AUTH_STATUS,
    STATE_STATUS,
    CONTENT_TYPES,
    XHR_ERRORS
} from "constants";

const INITIAL_STATE = {
    authStatus: AUTH_STATUS.UNAUTHORIZED,
    userStatus: STATE_STATUS.UNFETCHED,
    user: null,
    userError: null,
    authError: null,
    authToken: null,
    refreshToken: null,
    authPromise: null
};

const getUser = () =>
    axiosInstance.get(createUrl(["user", "me"]), {
        headers: {
            "Content-Type": CONTENT_TYPES.APPLICATION_JSON,
            Authorization: `Bearer ${getAuthToken().authToken}`
        }
    });

const getUserPermissions = () =>
    axiosInstance
        .get(createUrl(["user", "api-ui"]), {
            params: {
                paginate: false
            },
            headers: {
                "Content-Type": CONTENT_TYPES.APPLICATION_JSON,
                Authorization: `Bearer ${getAuthToken().authToken}`
            }
        })
        .then(_ => {
            return {
                ..._,
                data: _.data
                // data: new List(_.data)
                //     .toMap()
                //     .mapEntries(([, v]) => [v, true])
                //     .toJS()
                // data: _.data.reduce(
                //     (agg, curr) => ({ ...agg, [curr.name]: true }),
                //     {}
                // )
            };
        });


const logout = token => {
    let formData = new FormData();
    formData.append("client_id", CLIENT_ID);
    formData.append("client_secret", CLIENT_SECRET);
    formData.append("token", token.authToken);
    return axiosInstance.post("/o/revoke_token/", formData, {
        headers: {
            "Content-Type": "multipart/form-data"
        }
    });
};


class AuthServiceGenerator {
    static state = INITIAL_STATE;
    static hooks = [];
    static stateUpdatePipe = [];
    static clearUpdatePipeInterval = null;
    static setState = authStatus => {
        AuthServiceGenerator.stateUpdatePipe.push([
            authStatus => {
                AuthServiceGenerator.state.authStatus = authStatus;
                AuthServiceGenerator.hooks.forEach(func => func(authStatus));
            },
            authStatus
        ]);
        if (!AuthServiceGenerator.clearUpdatePipeInterval) {
            AuthServiceGenerator.clearUpdatePipeInterval = setInterval(() => {
                const [
                    firstStateUpdate,
                    stateName
                ] = AuthServiceGenerator.stateUpdatePipe[0];
                AuthServiceGenerator.stateUpdatePipe = AuthServiceGenerator.stateUpdatePipe.slice(
                    1
                );
                firstStateUpdate(stateName);
                if (AuthServiceGenerator.stateUpdatePipe.length === 0) {
                    clearInterval(AuthServiceGenerator.clearUpdatePipeInterval);
                    AuthServiceGenerator.clearUpdatePipeInterval = null;
                }
            }, 1500);
        }
    };

    constructor() {
        if (checkAuthToken()) {
            const { authToken, refreshToken } = getAuthToken();
            AuthServiceGenerator.state.authToken = authToken;
            AuthServiceGenerator.state.refreshToken = refreshToken;
            this.validateCredentials();
            return;
        }
        AuthServiceGenerator.setState(
            AUTH_STATUS.INVALID_AUTHORIZATION_CREDENTIALS
        );
    }
    authenticate = async (username, password, captcha_token, microsoftAuthResponse = null) => {
        let data;
        if (microsoftAuthResponse) {
            const { access_token, token_type, id_token } = microsoftAuthResponse;
            data = createFormData({
                grant_type: "microsoft",
                source: "SOUL",
                access_token,
                token_type,
                id_token,
            });
        } else {
            data = createFormData({
                username,
                password,
                captcha_token,
                grant_type: "password",
                client_id: CLIENT_ID,
                client_secret: CLIENT_SECRET,
                source: "SOUL",
            });
        }

        AuthServiceGenerator.setState(AUTH_STATUS.AUTHORIZING);

        return axios
            .post(`${BASE_URL}/o/token/v2/`, data, {
                headers: {
                    "Content-Type": CONTENT_TYPES.APPLICATION_JSON
                }
            })
            .then(({ data, error }) => {
                if (!error) {
                    const { access_token, refresh_token } = data;
                    setAuthToken(access_token, refresh_token);
                    AuthServiceGenerator.state.authToken = access_token;
                    AuthServiceGenerator.state.refreshToken = refresh_token;
                    AuthServiceGenerator.state.authError = null;
                    AuthServiceGenerator.setState(AUTH_STATUS.AUTHORIZED);
                } else {
                    AuthServiceGenerator.state.authError = error;
                    AuthServiceGenerator.setState(AUTH_STATUS.AUTHORIZATION_FAILED);
                }

                return Promise.resolve({ data, error });
            })
            .catch((error) => {
                return Promise.reject(error && error.response && error.response.data);
            });
    }
    sendOtp = (email) => {
        return axiosInstance.post(createUrl(["user", "forgot-password"]), {
            email
        });
    }

    changePassword = (data) => {
        return axiosInstance.post(createUrl(["user", "forgot-password", "reset"]), {
            ...data
        });
    }

    resetPassword = (current_password, new_password, confirm_new_password) => {
        const { authToken } = AuthServiceGenerator.state;
        return axiosInstance.post(createUrl(["user", "reset-password"]), {
            current_password,
            new_password,
            confirm_new_password
        }, {
            headers: {
                "Content-Type": CONTENT_TYPES.APPLICATION_JSON,
                Authorization: `Bearer ${authToken}`
            }
        });
    }
    validateCredentials = () => {
        const { authToken } = AuthServiceGenerator.state;
        AuthServiceGenerator.setState(
            AUTH_STATUS.VALIDATING_AUTHORIZATION_CREDENTIALS
        );
        if (authToken) {
            return axiosInstance
                .post(
                    createUrl(["user", "validate-token"]),
                    {},
                    {
                        headers: {
                            "Content-Type": CONTENT_TYPES.APPLICATION_JSON,
                            Authorization: `Bearer ${authToken}`
                        }
                    }
                )
                .then(_ => {
                    if (_ &&  _.error && _.error === XHR_ERRORS.INVALID_CREDENTIALS) {
                        AuthServiceGenerator.state.authError = null;
                        AuthServiceGenerator.state.authToken = null;
                        AuthServiceGenerator.state.refreshToken = null;
                        AuthServiceGenerator.setState(
                            AUTH_STATUS.INVALID_AUTHORIZATION_CREDENTIALS
                        );
                        removeAuthToken();
                        return;
                    }
                    AuthServiceGenerator.setState(AUTH_STATUS.AUTHORIZED);
                });
        }

        return;
    };
    getUser = () => {
        if (AuthServiceGenerator.state.authToken) {
            if (AuthServiceGenerator.state.user) {
                return Promise.resolve({
                    data: AuthServiceGenerator.state.user,
                    error: null
                });
            }
            return getUser().then(_ => {
                AuthServiceGenerator.state.user = _.data;
                return Promise.resolve(_);
            });
        }
        return Promise.resolve({
            data: null,
            error: AuthServiceGenerator.state.authStatus
        });
    };

    getUserPermissions = () => {
        if (AuthServiceGenerator.state.authToken) {
            return getUserPermissions();
        }
        return Promise.resolve({
            data: null,
            error: AuthServiceGenerator.state.authStatus
        });
    };

    logout = () => {
        // axiosInstance.post("/logout/")
        logout(getAuthToken());
        removeAuthToken();
        AuthServiceGenerator.state = {
            authStatus: AUTH_STATUS.UNAUTHORIZED,
            userStatus: STATE_STATUS.UNFETCHED,
            user: null,
            userError: null,
            authError: null,
            authToken: null,
            refreshToken: null,
            authPromise: null
        };
        return Promise.resolve(true);
    };
    isAuthenticated = () => {
        if (AuthServiceGenerator.state.authStatus === AUTH_STATUS.AUTHORIZED) {
            return true;
        }
        return false;
    };
    getState = () => AuthServiceGenerator.state.authStatus;
    getAuthToken = () => AuthServiceGenerator.state.authToken;
    onStatusChange = hook => {
        AuthServiceGenerator.hooks.push(hook);
        const hooksLastIndex = AuthServiceGenerator.hooks.length - 1;
        return () => {
            AuthServiceGenerator.hooks.filter(
                (_, index) => index !== hooksLastIndex
            );
            return null;
        };
    };
}

let instance = new AuthServiceGenerator();

export default instance;
